<template>
<div>
    <section class="pt-12 mb-6">
        <div class="container">
            <div class="row align-items-center">
                <div class="col-12 col-md-5 col-lg-6 order-md-2">
                    <img src="/assets/img/illustrations/illustration-7.png" class="img-fluid mw-md-150 mw-lg-130 mb-6 mb-md-0" alt="Instagram API - Access Instagram data through REST API">
                </div>
                <div class="col-12 col-md-7 col-lg-6 order-md-1">
                    <h1 class="display-4 text-center text-md-start">
                        Instagram API
                        <span class="text-primary d-block fs-2">
                            Fast Cache API for Instagram Data
                        </span>
                    </h1>
                    <p class="lead text-center text-md-start text-muted mb-4">
                        Reliable <a :href="`${$state.siteProto}://api.${$state.siteDomain}/docs`">REST API</a> to access Instagram profiles, posts, comments, stories and reels. No rate limits, no blocks.
                    </p>
                    <a :href="`${$state.siteProto}://api.${$state.siteDomain}/docs`" class="d-flex align-items-center rounded-3 px-3 py-2 mb-4 border border-secondary text-decoration-none" style="max-width: 420px; background: rgba(255,255,255,0.05);" target="_blank">
                        <i class="fe fe-lock text-muted me-2"></i>
                        <code class="text-success me-auto fs-5" style="letter-spacing: 0.3px;">{{ `${$state.siteProto}://api.${$state.siteDomain}` }}</code>
                        <span class="text-light ps-2 fs-5" style="border-left: 1px solid rgba(255,255,255,0.25);">Docs &#x203A;</span>
                    </a>
                    <ul class="list-unstyled mb-4">
                        <li class="d-flex align-items-start mb-2">
                            <i class="fe fe-check text-primary me-2 mt-1"></i>
                            <span><strong>User profiles</strong> — followers, following, bio, posts count, business contacts</span>
                        </li>
                        <li class="d-flex align-items-start mb-2">
                            <i class="fe fe-check text-primary me-2 mt-1"></i>
                            <span><strong>Posts & Reels</strong> — likes, comments, captions, media URLs</span>
                        </li>
                        <li class="d-flex align-items-start mb-2">
                            <i class="fe fe-check text-primary me-2 mt-1"></i>
                            <span><strong>Stories & Highlights</strong> — ephemeral content from public accounts</span>
                        </li>
                        <li class="d-flex align-items-start mb-2">
                            <i class="fe fe-check text-primary me-2 mt-1"></i>
                            <span><strong>Hashtags & Locations</strong> — discover trending content by topic or place</span>
                        </li>
                    </ul>
                    <p class="text-muted small mb-4">
                        All data is anonymously collected from public profiles. Business emails and phone numbers are available when users include them in their public profile.
                    </p>
                    <p class="text-success text-center text-md-start fw-bold mb-4" v-if="!$state.context.id">
                        <i class="fe fe-gift me-2"></i>{{ $state.context.free_requests }} free requests trial after Telegram verification
                    </p>
                    <div class="d-flex flex-column flex-md-row gap-2 justify-content-center justify-content-md-start" v-if="!$state.context.id">
                        <router-link :to="{path: '/login'}" class="btn btn-primary shadow lift">
                            Login
                        </router-link>
                        <router-link :to="{path: '/registration'}" class="btn btn-primary-soft lift">
                            Registration
                        </router-link>
                    </div>
                    <div class="text-center text-md-start" v-else>
                        <router-link :to="{path: '/dashboard'}" class="btn btn-primary shadow lift">
                            Console
                        </router-link>
                    </div>
                </div>
            </div>
        </div>
    </section>
    <section class="py-8 py-md-4">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-md-10 col-lg-10 text-center">
                    <h2>Simple, <span class="fw-bold text-primary-desat">transparent pricing</span></h2>
                    <p class="text-muted">
                        Pay per request. Choose data freshness that fits your needs — from real-time to cost-effective cached data.
                    </p>
                </div>
            </div>
        </div>
    </section>
    <section class="pt-1 pb-8 pt-md-2 pb-md-11">
        <Plans :currentPlanId="$state.context.plan?.id" />
    </section>
    <!-- Code Examples Section -->
    <section class="pt-2 pb-4 pt-md-4 pb-md-6">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-md-10 col-lg-8 text-center">
                    <h2 class="fw-bold">Quick Start</h2>
                    <p class="fs-lg text-muted mb-7">
                        Integrate in minutes with code examples for your favorite language
                    </p>
                </div>
            </div>
            <CodeExamples />
        </div>
    </section>
    <section class="pt-4 pb-8 pt-md-8">
        <div class="container">
            <div class="row align-items-center">
                <div class="col-12 col-md-5 col-lg-6 order-md-2">
                    <img src="assets/img/illustrations/illustration-3.png" alt="No scraping headaches - DataLikers handles proxy rotation and rate limits" class="img-fluid mb-6 mb-md-0">
                </div>
                <div class="col-12 col-md-7 col-lg-6 order-md-1">
                    <h2>
                        Skip the scraping headaches<br>
                        <span class="text-primary">We handle the complexity</span>
                    </h2>
                    <p class="fs-lg text-gray-700 mb-4">
                        No proxies to manage. No rate limits to worry about. No accounts getting blocked. Just clean data through a simple API call.
                    </p>
                    <ul class="list-unstyled mb-5">
                        <li class="d-flex align-items-center mb-2">
                            <i class="fe fe-shield text-success me-2"></i>
                            <span>Automatic proxy rotation and error handling</span>
                        </li>
                        <li class="d-flex align-items-center mb-2">
                            <i class="fe fe-zap text-success me-2"></i>
                            <span>Fast response times with intelligent caching</span>
                        </li>
                        <li class="d-flex align-items-center mb-2">
                            <i class="fe fe-lock text-success me-2"></i>
                            <span>Fully compliant with public data access</span>
                        </li>
                    </ul>
                    <div class="d-flex">
                        <div class="pe-5">
                            <h3 class="mb-0">
                                <span>99.9</span>%
                            </h3>
                            <p class="text-gray-700 mb-0">
                                Uptime
                            </p>
                        </div>
                        <div class="border-start border-gray-300"></div>
                        <div class="px-5">
                            <h3 class="mb-0">
                                <span>24/7</span>
                            </h3>
                            <p class="text-gray-700 mb-0">
                                Support
                            </p>
                        </div>
                        <div class="border-start border-gray-300"></div>
                        <div class="ps-5">
                            <h3 class="mb-0">
                                <span>&lt;500</span>ms
                            </h3>
                            <p class="text-gray-700 mb-0">
                                Avg. response
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
    <section class="py-6 py-md-8 bg-gray-200">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-md-10 col-lg-8 text-center">
                    <h2 v-if="!$state.context.id">
                        Ready to get started?
                    </h2>
                    <p class="fs-lg text-muted mb-4" v-if="!$state.context.id">
                        <router-link class="fw-bold text-primary" :to="{path: '/registration'}">Create an account</router-link> and start querying social media data within minutes.
                    </p>
                    <p class="fs-lg text-muted mb-4" v-else>
                        Go to <router-link class="fw-bold text-primary" :to="{path: '/dashboard'}">Console</router-link> to start querying social media data.
                    </p>
                    <p class="text-muted mb-7 mb-md-9">
                        Check out the <a :href="`${$state.siteProto}://api.${$state.siteDomain}/docs`">API documentation</a> for endpoints and examples.
                    </p>
                </div>
            </div>
        </div>
    </section>
</div>
</template>

<script>
import Plans from './Plans'
import CodeExamples from './CodeExamples'

export default {
    components: { Plans, CodeExamples },
    data() {
        return {}
    },
    computed: {
        prefix() {
            return `https://api.${location.hostname}`
        },
    },
    created() {
        if (this.$route.params && this.$route.params.promocode)
            localStorage.setItem("promocode", this.$route.params.promocode);
        if (document.referrer)
            localStorage.setItem("referrer", document.referrer);
        if (location.search.indexOf("paymentId") != -1) {
            this.$router.replace({path: '/billing'})
        }
    }
}
</script>
